import { h, inject, Transition, TransitionGroup } from 'vue';
import LitepieDatepicker from 'litepie-datepicker-tw3';
import { admin2026FloatingDatepickerDirective } from './admin2026FloatingDatepicker';
import { admin2026DrawerDatepickerDirective } from './admin2026DrawerDatepicker';

const configuredCalendarFirstDayOfWeek = () => {
    if (typeof document === 'undefined') {
        return 1;
    }

    const weekStartMeta = document.querySelector('meta[name="pono-calendar-first-day-of-week"]');
    const parsedWeekStart = Number.parseInt(weekStartMeta ? weekStartMeta.getAttribute('content') : '', 10);

    return parsedWeekStart === 7 ? 7 : 1;
};

const mondayFirstWeekdays = (weeks) => {
    if (!Array.isArray(weeks) || weeks.length < 7) {
        return weeks || [];
    }

    return weeks.slice(1, 7).concat(weeks.slice(0, 1));
};

const configuredWeekdays = (weeks) => {
    return configuredCalendarFirstDayOfWeek() === 7
        ? (weeks || [])
        : mondayFirstWeekdays(weeks);
};

const dateKey = (date) => {
    return date && typeof date.format === 'function'
        ? date.format('YYYY-MM-DD')
        : '';
};

const decoratedMissingCalendarDate = (date, monthStart, disabled = false) => {
    date.today = typeof date.isToday === 'function' ? date.isToday() : false;
    date.active = typeof date.isSame === 'function'
        ? date.isSame(monthStart, 'month')
        : date.month() === monthStart.month();
    date.off = !date.active;
    date.sunday = typeof date.day === 'function' ? date.day() === 0 : false;
    date.disabled = disabled;
    date.inRange = () => date.off;

    return date;
};

const mondayFirstCalendarDates = (dates) => {
    if (!Array.isArray(dates) || !dates.length) {
        return [];
    }

    const firstActiveDate = dates.find((date) => date && date.active && typeof date.date === 'function' && date.date() === 1)
        || dates.find((date) => date && date.active && typeof date.date === 'function');

    if (!firstActiveDate) {
        return dates;
    }

    const existingDates = new Map();

    dates.forEach((date) => {
        const key = dateKey(date);

        if (key) {
            existingDates.set(key, date);
        }
    });

    const monthStart = firstActiveDate.date(1);
    const calendarStart = monthStart.subtract((monthStart.day() + 6) % 7, 'day');
    const firstExistingDate = dates[0] || {};
    const missingDateDisabled = firstExistingDate.disabled === true;

    return Array.from({ length: 42 }, (value, index) => {
        const date = calendarStart.add(index, 'day');
        const key = dateKey(date);

        return existingDates.get(key) || decoratedMissingCalendarDate(date, monthStart, missingDateDisabled);
    });
};

const configuredCalendarDates = (dates) => {
    return configuredCalendarFirstDayOfWeek() === 7
        ? (dates || [])
        : mondayFirstCalendarDates(dates);
};

const PonoLitepieWeek = {
    ...LitepieDatepicker.components.LitepieWeek,
    name: 'PonoLitepieWeek',

    setup(props) {
        return () => h('div', {
            class: 'grid grid-cols-7 py-2 mt-0.5 border-b border-black/[.1] dark:border-litepie-secondary-700/[1]',
        }, configuredWeekdays(props.weeks).map((day, keyDay) => h('div', {
            key: keyDay,
            class: 'text-litepie-secondary-500 text-xs 2xl:text-sm tracking-wide font-medium text-center cursor-default dark:text-litepie-secondary-400',
        }, [
            h('span', {
                textContent: day,
            }),
        ])));
    },
};

const PonoLitepieCalendar = {
    ...LitepieDatepicker.components.LitepieCalendar,
    name: 'PonoLitepieCalendar',

    setup(props, { emit }) {
        const isBetweenRange = inject('isBetweenRange', () => false);
        const betweenRangeClasses = inject('betweenRangeClasses', () => '');
        const datepickerClasses = inject('datepickerClasses', () => '');
        const atMouseOver = inject('atMouseOver', () => false);

        return () => h('div', { class: 'grid grid-cols-7 gap-y-0.5 my-1' }, [
            h(TransitionGroup, {
                enterFromClass: 'opacity-0',
                enterToClass: 'opacity-100',
                enterActiveClass: 'transition-opacity ease-out duration-300',
                leaveActiveClass: 'transition-opacity ease-in duration-200',
                leaveFromClass: 'opacity-100',
                leaveToClass: 'opacity-0',
            }, () => (props.calendar && typeof props.calendar.date === 'function' ? configuredCalendarDates(props.calendar.date()) : []).map((date, keyDate) => {
                const duration = typeof date.duration === 'function' ? date.duration() : '';

                const hovered = typeof date.hovered === 'function' ? date.hovered() : false;

                return h('div', {
                    key: keyDate,
                    class: ['relative', {
                        'litepie-tooltip': props.asRange && duration,
                    }],
                    'data-tooltip': `${duration}`,
                }, [
                    h(Transition, {
                        enterFromClass: 'opacity-0',
                        enterToClass: 'opacity-100',
                        enterActiveClass: 'transition-opacity ease-out duration-200',
                        leaveActiveClass: 'transition-opacity ease-in duration-150',
                        leaveFromClass: 'opacity-100',
                        leaveToClass: 'opacity-0',
                    }, () => (isBetweenRange(date) || hovered) ? h('span', {
                        key: 0,
                        class: [
                            'absolute bg-litepie-primary-100 bg-opacity-60 dark:bg-litepie-secondary-700 dark:bg-opacity-50',
                            betweenRangeClasses(date),
                        ],
                    }) : null),
                    h('button', {
                        type: 'button',
                        class: [
                            'litepie-datepicker-date relative w-12 h-12 lg:w-10 lg:h-10 flex justify-center items-center text-xs 2xl:text-sm focus:outline-none',
                            [
                                datepickerClasses(date),
                                props.asRange ? 'transition-all' : 'transition-colors',
                            ],
                        ],
                        disabled: date.disabled,
                        onClick: () => emit('update:date', date, props.asPrevOrNext),
                        onMouseenter: () => atMouseOver(date),
                        onFocusin: () => atMouseOver(date),
                        'data-date': date.toDate(),
                    }, `${date.date()}`),
                ]);
            })),
        ]);
    },
};

const PonoLitepieDatepicker = {
    ...LitepieDatepicker,
    name: 'PonoLitepieDatepicker',
    setup(props, context) {
        // Litepie treats null as an object. Keep optional single dates blank
        // internally without changing the parent value or range-model behavior.
        const pickerProps = new Proxy(props, {
            get(target, key, receiver) {
                const value = Reflect.get(target, key, receiver);

                return key === 'modelValue' && target.asSingle && !target.useRange && value == null
                    ? ''
                    : value;
            },
        });

        return LitepieDatepicker.setup(pickerProps, context);
    },
    directives: {
        ...LitepieDatepicker.directives,
        litepie: admin2026FloatingDatepickerDirective(admin2026DrawerDatepickerDirective(LitepieDatepicker.directives.litepie)),
    },
    components: {
        ...LitepieDatepicker.components,
        LitepieWeek: PonoLitepieWeek,
        LitepieCalendar: PonoLitepieCalendar,
    },
};

export { PonoLitepieDatepicker as LitepieDatepicker };
export default PonoLitepieDatepicker;
